<template>
  <ActionPanelShell :title="panelTitle" :subtitle="subtitle" :show-back="showBack" @close="onClose" @back="onBack">
    <div class="sp cp" data-test="payment-panel" :data-step="step">
      <!-- ================================================================== -->
      <!-- SETUP — what is it for, how much, how will they pay                  -->
      <!-- ================================================================== -->
      <template v-if="step === 'setup'">
        <section class="cp-balance" data-test="cp-balance">
          <div>
            <div class="cp-eyebrow">Patient balance</div>
            <div class="cp-balance__v" :class="{ 'is-zero': !hasBalance }" data-test="cp-balance-value">{{ formatMoney(patientBalance) }}</div>
          </div>
          <span class="cp-balance__src">From Open Dental</span>
        </section>

        <section class="sp-sec" data-test="cp-purpose">
          <div id="cp-purpose-h" class="sp-h">What’s it for?</div>
          <div class="cp-choices" role="radiogroup" aria-labelledby="cp-purpose-h">
            <label class="cp-choice" :class="{ 'is-on': purpose === 'balance' }">
              <input v-model="purpose" type="radio" name="cp-purpose" value="balance" class="cp-choice__input" data-test="cp-purpose-balance">
              <span class="cp-choice__body">
                <span class="cp-choice__t">Outstanding balance</span>
                <span class="cp-choice__d">Collect toward what the patient owes</span>
              </span>
            </label>
            <label class="cp-choice" :class="{ 'is-on': purpose === 'deposit', 'is-off': !depositAvailable }">
              <input v-model="purpose" type="radio" name="cp-purpose" value="deposit" class="cp-choice__input" :disabled="!depositAvailable" data-test="cp-purpose-deposit">
              <span class="cp-choice__body">
                <span class="cp-choice__t">Treatment plan deposit</span>
                <span class="cp-choice__d">Posts to Open Dental as a prepayment</span>
                <span v-if="!depositAvailable" class="cp-why" data-test="cp-purpose-deposit-why">{{ depositDisabledReason }}</span>
              </span>
            </label>
          </div>
        </section>

        <section class="sp-sec" data-test="cp-amount-sec">
          <div>
            <label class="sp-label" for="cp-amount">Amount to collect</label>
            <div class="cp-money">
              <span class="cp-money__sym" aria-hidden="true">$</span>
              <input
                id="cp-amount"
                ref="amountInput"
                v-model.number="amount"
                type="number"
                class="sp-input cp-money__input"
                :min="0.01"
                :max="maxAmount"
                step="0.01"
                inputmode="decimal"
                placeholder="0.00"
                :aria-invalid="amountError ? 'true' : 'false'"
                aria-describedby="cp-amount-hint"
                data-test="cp-amount"
              >
            </div>
            <p id="cp-amount-hint" class="sp-note cp-hint" :class="{ 'sp-note--bad': !!amountError }" data-test="cp-amount-hint">{{ amountError || amountHint }}</p>
          </div>

          <div v-if="isDepositPurpose && signedPlanOptions.length > 1">
            <label class="sp-label" for="cp-plan">Apply to a signed treatment plan (optional)</label>
            <select id="cp-plan" v-model="selectedTreatPlanNum" class="cp-select" data-test="cp-plan">
              <option v-for="o in signedPlanOptions" :key="String(o.value)" :value="o.value">{{ o.text }}</option>
            </select>
          </div>
        </section>

        <section class="sp-sec" data-test="cp-methods">
          <div id="cp-method-h" class="sp-h">How will {{ firstName }} pay?</div>
          <div class="cp-methods" role="group" aria-labelledby="cp-method-h">
            <button
              v-for="m in methodCards"
              :key="m.key"
              type="button"
              class="cp-method"
              :class="{ 'is-on': m.on, 'is-off': !m.available }"
              :aria-pressed="m.on ? 'true' : 'false'"
              :disabled="!m.available"
              :data-test="`cp-method-${m.key}`"
              @click="selectedMethod = m.key"
            >
              <span class="cp-method__ic" aria-hidden="true"><i class="fa" :class="m.icon"></i></span>
              <span class="cp-method__t">{{ m.title }}</span>
              <span class="cp-method__d">{{ m.sub }}</span>
              <span v-if="!m.available" class="cp-why" :data-test="`cp-method-${m.key}-why`">{{ m.reason }}</span>
              <i v-if="m.on" class="fa fa-check-circle cp-method__check" aria-hidden="true"></i>
            </button>
          </div>
        </section>
      </template>

      <!-- ================================================================== -->
      <!-- TERMINAL STATE — one thing at a glance, the amount always in view     -->
      <!-- ================================================================== -->
      <section v-else-if="step === 'terminal-state'" class="cp-state" :data-state="terminalChargeState" data-test="cp-state">
        <template v-if="terminalChargeInFlight">
          <span class="cp-spin" aria-hidden="true"></span>
          <div v-if="terminalChargeState === 'sending'" class="cp-state__t" data-test="cp-state-title">Sending to the terminal…</div>
          <div v-else-if="terminalChargeState === 'waiting'" class="cp-state__t" data-test="cp-state-title">Waiting for the card</div>
          <div v-else class="cp-state__t" data-test="cp-state-title">{{ terminalChargeMessage || 'Checking status…' }}</div>
          <div class="cp-state__amt" data-test="cp-state-amount">{{ formatMoney(amount) }}</div>
          <p class="cp-state__d" data-test="cp-state-where">On <b>{{ selectedTerminalName || 'the terminal' }}</b></p>
          <p class="cp-state__n">
            A swipe can take up to 90 seconds. To stop it on the device too, press <b>Cancel</b> on the terminal.
          </p>
        </template>

        <template v-else-if="terminalChargeState === 'success'">
          <span class="cp-state__ic is-ok" aria-hidden="true"><i class="fa fa-check"></i></span>
          <div class="cp-state__t" data-test="cp-state-title">Payment captured</div>
          <!-- The amount the CARD gave, not the amount typed (collectedAmount): on a partial approval the typed
               amount hid a $117.86 shortfall (Tulip Tree 8/18). -->
          <div class="cp-state__amt" data-test="cp-state-amount">{{ formatMoney(collectedAmount) }}</div>
          <p v-if="isDepositPurpose" class="cp-state__n">Recorded in Open Dental as a treatment plan prepayment.</p>
          <p v-if="terminalChargeTransactionId" class="cp-state__n" data-test="cp-state-txn">Txn {{ terminalChargeTransactionId }}</p>
        </template>

        <!-- Partially approved: the card had less than we asked for. Moolah reports it as an ordinary success,
             so this state has to shout — the patient is still at the desk and the rest is collectable now. -->
        <template v-else-if="terminalChargeState === 'partial'">
          <span class="cp-state__ic is-warn" aria-hidden="true"><i class="fa fa-exclamation-triangle"></i></span>
          <div class="cp-state__t" data-test="cp-state-title">Partially approved</div>
          <div class="cp-state__amt" data-test="cp-state-amount">{{ formatMoney(terminalCapturedAmount) }}</div>
          <p class="cp-state__d">
            The card only had {{ formatMoney(terminalCapturedAmount) }} available. That’s common with HSA, FSA and prepaid cards.
          </p>
          <div class="cp-breakdown" data-test="cp-partial">
            <div class="cp-breakdown__row"><span>Requested</span><span data-test="cp-partial-requested">{{ formatMoney(terminalRequestedAmount) }}</span></div>
            <div class="cp-breakdown__row"><span>Approved on this card</span><span data-test="cp-partial-approved">{{ formatMoney(terminalCapturedAmount) }}</span></div>
            <div class="cp-breakdown__row is-due"><span>Not collected</span><span data-test="cp-partial-due">{{ formatMoney(terminalShortfall) }}</span></div>
          </div>
          <p class="cp-state__n">
            Open Dental was posted for {{ formatMoney(terminalCapturedAmount) }}, so the remaining {{ formatMoney(terminalShortfall) }} is still owed.
            Charge the rest on another card while {{ firstName }} is still here.
          </p>
          <p v-if="terminalChargeTransactionId" class="cp-state__n" data-test="cp-state-txn">Txn {{ terminalChargeTransactionId }}</p>
        </template>

        <template v-else-if="terminalChargeState === 'declined'">
          <span class="cp-state__ic is-bad" aria-hidden="true"><i class="fa fa-times"></i></span>
          <div class="cp-state__t" data-test="cp-state-title">Card declined</div>
          <div class="cp-state__amt" data-test="cp-state-amount">{{ formatMoney(amount) }}</div>
          <p class="cp-state__d">{{ terminalChargeMessage || 'The card was declined.' }}</p>
        </template>

        <template v-else-if="terminalChargeState === 'cancelled'">
          <span class="cp-state__ic is-mute" aria-hidden="true"><i class="fa fa-ban"></i></span>
          <div class="cp-state__t" data-test="cp-state-title">{{ cancelledByOperator ? 'Charge cancelled' : 'Cancelled at the terminal' }}</div>
          <div class="cp-state__amt" data-test="cp-state-amount">{{ formatMoney(amount) }}</div>
          <p v-if="cancelledByOperator" class="cp-state__d">
            We’ve stopped tracking this charge. <b>Press Cancel on the terminal device</b> to abort the swipe there too,
            otherwise the patient could still complete it. Verify in Moolah reports.
          </p>
          <p v-else class="cp-state__d">{{ terminalChargeMessage || 'The transaction was cancelled.' }}</p>
        </template>

        <template v-else-if="terminalChargeState === 'error'">
          <span class="cp-state__ic is-warn" aria-hidden="true"><i class="fa fa-exclamation-triangle"></i></span>
          <div class="cp-state__t" data-test="cp-state-title">Something went wrong</div>
          <div class="cp-state__amt" data-test="cp-state-amount">{{ formatMoney(amount) }}</div>
          <p class="cp-state__d">{{ terminalChargeMessage || 'Failed to complete the charge.' }}</p>
        </template>

        <template v-else-if="terminalChargeState === 'blocked'">
          <span class="cp-state__ic is-warn" aria-hidden="true"><i class="fa fa-hand-paper-o"></i></span>
          <div class="cp-state__t" data-test="cp-state-title">Charge not sent</div>
          <div class="cp-state__amt" data-test="cp-state-amount">{{ formatMoney(amount) }}</div>
          <p class="cp-state__d">{{ terminalChargeMessage }}</p>
          <p class="cp-state__n">Nothing reached the terminal, so <b>this attempt did not charge any card</b>.</p>
        </template>

        <template v-else-if="terminalChargeState === 'unconfirmed'">
          <span class="cp-state__ic is-warn" aria-hidden="true"><i class="fa fa-question-circle"></i></span>
          <div class="cp-state__t" data-test="cp-state-title">Charge not confirmed</div>
          <div class="cp-state__amt" data-test="cp-state-amount">{{ formatMoney(amount) }}</div>
          <p class="cp-state__d">{{ terminalChargeMessage || 'We could not confirm this charge.' }}</p>
          <PanelNotice tone="warn" icon="fa-exclamation-triangle" class="cp-state__notice" data-test="cp-unconfirmed-warning">
            <b>Do not re-charge this patient</b> unless you have confirmed in Moolah that the card was not charged. A card swiped
            after the wait can still go through, and it will post here automatically if it did.
          </PanelNotice>
          <p class="cp-state__n">
            This charge keeps the terminal reserved for up to an hour. If you’ve confirmed the card was <b>not</b> charged,
            press <b>Cancel charge</b> to free it for the next patient.
          </p>
        </template>
      </section>

      <!-- ================================================================== -->
      <!-- CONFIGURE — the amount stays in view; one section per channel          -->
      <!-- ================================================================== -->
      <template v-else>
        <section class="cp-strip" data-test="cp-strip">
          <span class="cp-strip__ic" aria-hidden="true"><i class="fa" :class="methodIcon"></i></span>
          <div class="cp-strip__text">
            <div class="cp-strip__amt" data-test="cp-amount-line">{{ formatMoney(amount) }}</div>
            <div class="cp-strip__sum" data-test="cp-summary">{{ summaryLine }}</div>
          </div>
          <button type="button" class="sp-link" :disabled="sendingInProgress" data-test="cp-change" @click="onBack">Change</button>
        </section>

        <!-- Text -->
        <section v-if="step === 'sms-configure' || step === 'both-configure'" class="sp-sec" data-test="cp-sms-sec">
          <div class="sp-head">
            <span class="sp-h">Text message</span>
            <span class="sp-aside">To {{ patientPhoneFormatted }}</span>
          </div>
          <div>
            <label class="sp-label" for="cp-sms-template">Template</label>
            <select id="cp-sms-template" v-model="selectedSmsTemplateID" class="cp-select" data-test="cp-sms-template" @change="onSmsTemplatePicked">
              <option :value="null" disabled>Select a template…</option>
              <option v-for="t in paymentSmsTemplates" :key="t.WikiPageID" :value="t.WikiPageID">{{ t.Name }}</option>
            </select>
            <p v-if="paymentSmsTemplates.length === 0" class="sp-note cp-hint" data-test="cp-sms-no-templates">
              No payment text templates found. Tag a wiki page as “payment” in Wiki to create one.
            </p>
          </div>
          <div>
            <label class="sp-label" for="cp-sms-text">Message</label>
            <textarea
              id="cp-sms-text"
              v-model="smsText"
              rows="5"
              class="cp-textarea"
              placeholder="Pick a template above to populate, or write your own…"
              data-test="cp-sms-text"
            ></textarea>
            <!-- Shared SMS guidance: the live credit counter + the warn / block banner (template-aware: {{payment_link}}). -->
            <SmsGuidance :text="smsText || ''" substitute-tags @change="onSmsGuidance" />
          </div>
          <p class="sp-note">
            <code v-pre>{{payment_link}}</code> turns into {{ firstName }}’s secure payment link when it sends. The template comes from Settings, Payments.
          </p>
          <PanelNotice v-if="smsOutboundOff" tone="warn" icon="fa-ban" data-test="cp-sms-outbound-off">
            Texting is turned off for this office. Send the payment link by email instead.
          </PanelNotice>
          <PanelNotice v-if="smsOptOutBlock.blocked" tone="warn" icon="fa-user-times" data-test="cp-sms-optout">{{ smsOptOutBlock.message }}</PanelNotice>
          <PanelNotice v-if="phoneProblem" tone="warn" icon="fa-phone" data-test="cp-phone-problem">{{ phoneProblem }}</PanelNotice>
        </section>

        <!-- Email -->
        <section v-if="step === 'email-configure' || step === 'both-configure'" class="sp-sec" data-test="cp-email-sec">
          <div class="sp-head">
            <span class="sp-h">Email</span>
            <span class="sp-aside">To {{ patientEmail || '(no email)' }}</span>
          </div>
          <div>
            <label class="sp-label" for="cp-email-template">Template</label>
            <select id="cp-email-template" v-model="selectedEmailTemplateID" class="cp-select" data-test="cp-email-template">
              <option :value="null" disabled>Select a template…</option>
              <option v-for="t in paymentEmailTemplates" :key="t.WikiPageID" :value="t.WikiPageID">{{ t.Name }}</option>
            </select>
            <p v-if="paymentEmailTemplates.length === 0" class="sp-note cp-hint" data-test="cp-email-no-templates">
              No payment email templates found. Tag a wiki page as both “payment” and “email-template” in Wiki.
            </p>
            <p v-else class="sp-note cp-hint">
              The email body comes from the Wiki template. <code v-pre>{{payment_link}}</code> is filled in when it sends.
            </p>
          </div>
        </section>

        <!-- Terminal -->
        <template v-if="step === 'terminal-configure'">
          <section class="sp-sec" data-test="cp-terminal-sec">
            <div class="sp-head">
              <span class="sp-h">Terminal</span>
              <button
                v-if="!terminalDevicesLoading && terminalDevices.length > 0"
                type="button"
                class="sp-link cp-refresh"
                data-test="cp-terminals-refresh"
                @click="fetchTerminalDevices"
              ><i class="fa fa-refresh" aria-hidden="true"></i> Refresh</button>
            </div>

            <p v-if="terminalDevicesLoading" class="sp-note" data-test="cp-terminals-loading">
              <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading terminals…
            </p>

            <div v-else-if="terminalDevicesError" class="cp-empty" data-test="cp-terminals-error">
              <PanelNotice tone="error" icon="fa-exclamation-triangle">
                <b>Couldn’t load terminals.</b> {{ terminalDevicesError }}
              </PanelNotice>
              <div>
                <button type="button" class="sp-btn-secondary" data-test="cp-terminals-retry" @click="fetchTerminalDevices">
                  <i class="fa fa-refresh" aria-hidden="true"></i> Try again
                </button>
              </div>
            </div>

            <div v-else-if="terminalDevices.length === 0" class="cp-empty" data-test="cp-terminals-empty">
              <PanelNotice tone="warn" icon="fa-credit-card">
                <b>No active terminals.</b> No <b>ACTIVATED</b> terminals are registered with Moolah for this site.
                Activate one in your Moolah portal, or pick a different payment method.
              </PanelNotice>
              <div class="cp-empty__actions">
                <button type="button" class="sp-btn-secondary" data-test="cp-terminals-empty-refresh" @click="fetchTerminalDevices">
                  <i class="fa fa-refresh" aria-hidden="true"></i> Refresh
                </button>
                <button type="button" class="sp-btn-ghost" data-test="cp-terminals-empty-back" @click="onBack">
                  <i class="fa fa-arrow-left" aria-hidden="true"></i> Pick another method
                </button>
              </div>
            </div>

            <div v-else class="cp-terms" role="radiogroup" aria-label="Terminal" data-test="cp-terminal-list">
              <label v-for="d in terminalDevices" :key="d.id" class="cp-term" :class="{ 'is-on': selectedTerminalId === d.id }" :data-test="`cp-terminal-${d.id}`">
                <input v-model="selectedTerminalId" type="radio" name="cp-terminal" :value="d.id" class="cp-term__input">
                <span class="cp-term__body">
                  <span class="cp-term__n">
                    {{ d.name }}
                    <span v-if="d.isVirtual" class="cp-badge" title="Virtual Terminal — software-only, intended for testing">Virtual — test only</span>
                  </span>
                  <span v-if="d.serialNumber" class="cp-term__sn">SN {{ d.serialNumber }}</span>
                </span>
              </label>
            </div>
          </section>

          <section v-if="terminalDevices.length > 0" class="sp-sec" data-test="cp-receipt-sec">
            <div class="sp-h">Receipt</div>
            <label class="cp-check" :class="{ 'is-on': receiptShowOnTerminal }">
              <input v-model="receiptShowOnTerminal" type="checkbox" class="cp-check__box" data-test="cp-receipt-screen">
              <span class="cp-check__body">
                <span class="cp-check__t">Show the receipt on the terminal screen</span>
                <span class="cp-check__d">{{ firstName }} sees the amount and tap-to-pay prompts on the device.</span>
              </span>
            </label>
            <label class="cp-check" :class="{ 'is-on': receiptEmail }">
              <input v-model="receiptEmail" type="checkbox" class="cp-check__box" data-test="cp-receipt-email">
              <span class="cp-check__body">
                <span class="cp-check__t">Email a receipt to {{ firstName }}</span>
                <span class="cp-check__d">Moolah sends it once the charge goes through.</span>
              </span>
            </label>
            <div v-if="receiptEmail" class="cp-receipt-addr">
              <PanelNotice v-if="!patientEmail" tone="warn" data-test="cp-receipt-no-email">No email on file. Enter one below if they want a receipt.</PanelNotice>
              <input
                v-model="receiptEmailAddress"
                type="email"
                class="sp-input"
                placeholder="patient@example.com"
                aria-label="Receipt email address"
                data-test="cp-receipt-address"
              >
            </div>
          </section>
        </template>
      </template>
    </div>

    <template #footer>
      <!-- A confirm, in the panel: the classic asked these three questions with a browser dialog. -->
      <template v-if="confirm">
        <span class="cp-confirm-text" role="alert" data-test="cp-confirm">{{ confirmText }}</span>
        <button type="button" class="cp-foot-ghost" data-test="cp-confirm-no" @click="answerNo">{{ confirmNoLabel }}</button>
        <button type="button" class="cp-foot-main ap-foot-main" data-test="cp-confirm-yes" @click="answerYes">{{ confirmYesLabel }}</button>
      </template>

      <template v-else>
        <span v-if="footNote" class="ap-foot-note" :class="{ 'is-warn': footWarn }" data-test="cp-foot-note">{{ footNote }}</span>
        <button
          v-for="b in sideActions"
          :key="b.key"
          type="button"
          :class="{ 'cp-foot-ghost': b.tone === 'ghost', 'cp-foot-secondary': b.tone === 'secondary', 'cp-foot-danger': b.tone === 'danger' }"
          :disabled="!!b.disabled"
          :data-test="`cp-foot-${b.key}`"
          @click="b.run()"
        >
          <i v-if="b.busy" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          <i v-else-if="b.icon" class="fa" :class="b.icon" aria-hidden="true"></i>
          {{ b.label }}
        </button>
        <button
          v-if="mainAction"
          type="button"
          class="cp-foot-main ap-foot-main"
          :disabled="!!mainAction.disabled"
          data-test="cp-main"
          @click="mainAction.run()"
        >
          <i v-if="mainAction.busy" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          <i v-else-if="mainAction.icon && !mainAction.after" class="fa" :class="mainAction.icon" aria-hidden="true"></i>
          {{ mainAction.label }}
          <i v-if="mainAction.icon && mainAction.after && !mainAction.busy" class="fa" :class="mainAction.icon" aria-hidden="true"></i>
        </button>
      </template>
    </template>
  </ActionPanelShell>
</template>

<script>
/**
 * Collect payment — the redesigned panel (design "Patient Action Panels" 2j), opened by
 * PatientPanelActionHost.run('pay') through openActionPanel(… { lockable: true }) (480px, 'ap-slideout').
 *
 * `extends` CollectPaymentModal. EVERY rule that moves money is the classic code, inherited as it is:
 * the amount gate (amountOk / the balance cap / the deposit rules), the method gates and their disabled
 * reasons, the pending-record + SMS / email / both sends and their refusal handling, the terminal charge
 * (receipt options, 409 "blocked", the 8-second axios give-up -> 240-second polling window, the live
 * recovery query, "unconfirmed" = never a blind re-charge), the partial-approval facts and "charge the
 * remainder" (a NEW payment for the shortfall, never the original amount), and the payloads of all of it
 * (tests/component/CollectPaymentPanel.spec.js mounts the classic beside the panel and compares them).
 * Only the shell around them is new:
 *
 *   - Same steps, drawn as a panel: setup (what is it for, how much, how will they pay) -> a configure step
 *     per channel (a "Change" link and the back arrow return to setup) -> the terminal state.
 *   - A method that cannot be used says WHY on its card (the classic's own reason strings).
 *   - THE AMOUNT STAYS IN VIEW on every step: the field, then a strip, then the state's big number.
 *   - Terminal states read at a glance: one icon, one title, the amount, one or two sentences. The partial
 *     approval keeps the classic's requested / approved / not-collected table.
 *   - ONE main button in the footer, naming exactly what it will do ("Text the payment link", "Send $45.00
 *     to terminal", "Charge remaining $12.00"); the footer note says why it is off.
 *
 * CLOSING. The classic modal hid itself with `$emit('input', false)`; a panel closes with `closePanel`. The
 * `show` computed is overridden so every inherited `this.show = false` (a send that went out, Done) lands in
 * dismiss(). And no browser dialog anywhere: the three questions the classic asked with a confirm dialog are
 * a confirm ROW in the footer —
 *     - closing while a charge is in flight  (closeModal)
 *     - "Stop waiting for this terminal charge?"            (cancelInFlightCharge)
 *     - "Cancel this unconfirmed charge?"                   (cancelInFlightCharge(true))
 *   closeModal is rewritten for that. cancelInFlightCharge is split in two: it now ASKS, and doCancelCharge —
 *   the classic's body from "this.cancellingCharge = true" down, line for line — runs on Yes.
 *
 * LOCK. While money is moving (a terminal charge in flight, a send, the backend cancel) the host's
 * setCloseLocked(true) makes the scrim and Escape do nothing, exactly as the classic b-modal's
 * no-close-on-backdrop / no-close-on-esc did. The X: mid-send and mid-cancel it is ignored; mid-charge it
 * asks (the classic's closeModal sentence — closing does not abort the charge). The charge keeps going if
 * the operator closes anyway, as it always did; reopening is the way back to it (Moolah reports).
 *
 * The classic has NO mixin.js (its patNum / siteId props cannot be shadowed), so they are inherited as they
 * are; the only props added are `setCloseLocked` and a `value` that defaults to true (a panel is simply open).
 *
 * ⛔ No browser dialog in this file, no `$emit('input')`, no `$showPanel`, no /runquery
 * (patient-action-panels.test.js pins all four).
 */
import CollectPaymentModal from '@/pages/PatientProfile/components/CollectPaymentModal.vue';
import SmsGuidance from '@/components/SmsGuidance.vue';
import ActionPanelShell from './ActionPanelShell.vue';
import PanelNotice from './PanelNotice.vue';

const CONFIG_STEPS = ['sms-configure', 'email-configure', 'both-configure', 'terminal-configure'];

export default {
  name: 'CollectPaymentPanel',

  extends: CollectPaymentModal,

  components: { ActionPanelShell, PanelNotice, SmsGuidance },

  props: {
    // Required on the modal (its b-modal v-model); a panel is simply open.
    value: { type: Boolean, default: true },
    /** From the host (lockable): lock the slide-out's scrim / Escape while money is moving. */
    setCloseLocked: { type: Function, default: null },
  },

  data() {
    return {
      dismissed: false,
      /** null | { kind: 'close' | 'cancel-charge' | 'cancel-unconfirmed' } — the confirm row in the footer. */
      confirm: null,
    };
  },

  computed: {
    // The classic's `show` was the b-modal's v-model (value / emit input). Here "open" is "not dismissed", and
    // every inherited `this.show = false` closes the panel.
    show: {
      get() { return !this.dismissed; },
      set(v) { if (!v) this.dismiss(); },
    },

    firstName() {
      const p = this.patientData || {};
      return String(p.FName || '').trim() || 'the patient';
    },
    subtitle() {
      const p = this.patientData || {};
      const name = [p.FName, p.LName].filter(Boolean).join(' ').trim() || p.PatientName || '';
      return name ? `For ${name}` : '';
    },
    panelTitle() {
      return this.step === 'terminal-state' ? 'Terminal charge' : 'Collect payment';
    },
    isConfigStep() {
      return CONFIG_STEPS.includes(this.step);
    },
    showBack() {
      return this.isConfigStep;
    },

    // ---- setup ---------------------------------------------------------------------------------------

    amountBlank() {
      return this.amount === '' || this.amount === null || this.amount === undefined;
    },
    amountError() {
      if (this.amountExceedsCap) return `Can’t be more than the balance, ${this.formatMoney(this.maxAmount)}.`;
      if (!this.amountValid && !this.amountBlank) return 'Enter an amount greater than zero.';
      return '';
    },
    amountHint() {
      return this.isDepositPurpose
        ? `Posts to Open Dental as an unearned prepayment credit on ${this.firstName}’s account.`
        : 'Full balance by default. Lower it to collect part of it.';
    },

    // The four ways to collect, each with the classic's own can* gate and disabled reason.
    methodCards() {
      const first = this.firstName;
      const both = this.canSms && this.canEmail;
      return [
        { key: 'sms', title: 'Text a link', sub: `${first} pays from their phone`, icon: 'fa-mobile', available: this.canSms, reason: this.smsDisabledReason },
        { key: 'email', title: 'Email a link', sub: `${first} pays from their inbox`, icon: 'fa-envelope', available: this.canEmail, reason: this.emailDisabledReason },
        { key: 'both', title: 'Text and email', sub: 'Sends the link both ways', icon: 'fa-paper-plane', available: both, reason: !this.canSms ? this.smsDisabledReason : this.emailDisabledReason },
        { key: 'terminal', title: 'Card terminal', sub: `${first} taps or swipes here`, icon: 'fa-credit-card', available: this.canTerminal, reason: this.terminalDisabledReason },
      ].map(m => ({ ...m, on: this.selectedMethod === m.key && m.available }));
    },

    // ---- configure -----------------------------------------------------------------------------------

    methodIcon() {
      return { 'sms-configure': 'fa-mobile', 'email-configure': 'fa-envelope', 'both-configure': 'fa-paper-plane', 'terminal-configure': 'fa-credit-card' }[this.step] || 'fa-credit-card';
    },
    summaryLine() {
      const to = {
        'sms-configure': `Text to ${this.patientPhoneFormatted}`,
        'email-configure': `Email to ${this.patientEmail || '(no email)'}`,
        'both-configure': 'Text and email',
        'terminal-configure': 'Card terminal',
      }[this.step] || '';
      return `${this.purposeLabel} · ${to}`;
    },

    // ---- the footer ----------------------------------------------------------------------------------

    /** Money is moving: a terminal charge in flight, a link going out, the backend cancel. */
    lockNeeded() {
      return !!(this.terminalChargeInFlight || this.sendingInProgress || this.cancellingCharge || this.chargingRemainder);
    },

    smsNote() {
      if (this.sendingInProgress) return 'Sending…';
      if (this.canSendSms) return '';
      if (this.smsOutboundOff) return 'Texting is off for this office';
      if (this.smsOptOutBlock.blocked) return 'This patient opted out of texts';
      if (this.phoneProblem) return 'Fix the phone number first';
      if (!this.selectedSmsTemplateID) return 'Pick a template first';
      if (!(this.smsText || '').trim()) return 'Write the message first';
      if (this.smsBlocked) return 'Too long to send as one text';
      return '';
    },
    emailNote() {
      if (this.sendingInProgress) return 'Sending…';
      return this.selectedEmailTemplateID ? '' : 'Pick a template first';
    },
    terminalNote() {
      if (this.terminalDevicesLoading) return 'Loading terminals…';
      if (!this.terminalDevices.length) return 'No terminal to send to';
      if (!this.selectedTerminalId) return 'Pick a terminal';
      if (this.receiptEmail && !(this.receiptEmailAddress || '').trim()) return 'Enter the receipt email';
      return '';
    },
    footNote() {
      switch (this.step) {
        case 'setup':
          if (!this.amountOk) return this.amountExceedsCap ? `Up to ${this.formatMoney(this.maxAmount)}` : 'Enter an amount to continue';
          if (!this.selectedMethod) return `Pick how ${this.firstName} will pay`;
          return `Collecting ${this.formatMoney(this.amount)}`;
        case 'sms-configure': return this.smsNote;
        case 'email-configure': return this.emailNote;
        case 'both-configure': return this.smsNote || this.emailNote;
        case 'terminal-configure': return this.terminalNote;
        case 'terminal-state': return this.terminalChargeInFlight ? 'A charge is in progress' : '';
        default: return '';
      }
    },
    /** The note explains an OFF main button (amber); "Collecting $45.00" and "Sending…" are just information. */
    footWarn() {
      return !!this.footNote && !!this.mainAction && !!this.mainAction.disabled && !this.sendingInProgress;
    },

    /** The footer's ONE main button for this step, naming what it will do. null: this step has none. */
    mainAction() {
      switch (this.step) {
        case 'setup':
          return { label: 'Continue', icon: 'fa-arrow-right', after: true, disabled: !this.canContinue, run: this.onContinue };
        case 'sms-configure':
          return { label: this.sendingInProgress ? 'Sending…' : 'Text the payment link', icon: 'fa-mobile', busy: this.sendingInProgress, disabled: !this.canSendSms, run: this.sendSmsOnly };
        case 'email-configure':
          return { label: this.sendingInProgress ? 'Sending…' : 'Email the payment link', icon: 'fa-envelope', busy: this.sendingInProgress, disabled: !this.canSendEmail, run: this.sendEmailOnly };
        case 'both-configure':
          return { label: this.sendingInProgress ? 'Sending…' : 'Send text and email', icon: 'fa-paper-plane', busy: this.sendingInProgress, disabled: !this.canSendBoth, run: this.sendBoth };
        case 'terminal-configure':
          return { label: `Send ${this.formatMoney(this.amount)} to terminal`, icon: 'fa-credit-card', disabled: !this.canSendToTerminal, run: this.sendToTerminal };
        case 'terminal-state':
          return this.stateButtons.main;
        default:
          return null;
      }
    },
    /** The other footer buttons, left of the main one. */
    sideActions() {
      return this.step === 'terminal-state' ? this.stateButtons.side : [];
    },

    // What each terminal state offers. The classic's buttons, in the classic's order of importance.
    stateButtons() {
      const done = { label: 'Done', run: this.closeModal };
      const retry = { label: 'Try again', icon: 'fa-refresh', run: this.resetTerminalToConfigure };
      const check = {
        key: 'check',
        label: 'Check status',
        icon: 'fa-refresh',
        busy: this.checkingStatus,
        disabled: this.checkingStatus || !this.terminalPaymentID,
        run: this.manualCheckStatus,
      };
      switch (this.terminalChargeState) {
        case 'sending':
        case 'waiting':
        case 'polling':
          return {
            main: null,
            side: [
              { ...check, tone: 'secondary' },
              { key: 'cancel-charge', label: 'Cancel charge', icon: 'fa-ban', tone: 'danger', disabled: this.cancellingCharge, busy: this.cancellingCharge, run: () => this.cancelInFlightCharge() },
            ],
          };
        case 'success':
          return { main: done, side: [] };
        case 'partial':
          return {
            main: {
              label: `Charge remaining ${this.formatMoney(this.terminalShortfall)}`,
              icon: 'fa-credit-card',
              busy: this.chargingRemainder,
              disabled: this.chargingRemainder,
              run: this.chargeRemainder,
            },
            side: [{ key: 'done', label: 'Done for now', tone: 'ghost', run: this.closeModal }],
          };
        case 'declined':
        case 'error':
          return { main: retry, side: [{ key: 'close', label: 'Close', tone: 'ghost', run: this.closeModal }] };
        case 'cancelled':
          return { main: retry, side: [{ key: 'done', label: 'Done', tone: 'ghost', run: this.closeModal }] };
        // "Done" is the main button on purpose: retrying right away just gets refused while the other charge is live.
        case 'blocked':
          return { main: done, side: [{ key: 'retry', label: 'Try again', icon: 'fa-refresh', tone: 'secondary', run: this.resetTerminalToConfigure }] };
        case 'unconfirmed':
          return {
            main: check,
            side: [
              { key: 'done', label: 'Done', tone: 'ghost', run: this.closeModal },
              { key: 'cancel-charge', label: 'Cancel charge', icon: 'fa-ban', tone: 'danger', disabled: this.cancellingCharge || !this.terminalPaymentID, busy: this.cancellingCharge, run: () => this.cancelInFlightCharge(true) },
            ],
          };
        default:
          return { main: null, side: [] };
      }
    },

    // ---- the confirm row -----------------------------------------------------------------------------

    confirmText() {
      const c = this.confirm;
      if (!c) return '';
      if (c.kind === 'close') {
        return `Payment is in progress on ${this.selectedTerminalName || 'the terminal'}. `
          + 'Closing won’t abort the charge. Verify in Moolah reports if it goes through. Close anyway?';
      }
      if (c.kind === 'cancel-unconfirmed') {
        return 'Cancel this unconfirmed charge? Only do this if you’ve confirmed the card was NOT charged. '
          + 'Also press Cancel/Back on the terminal, otherwise the patient could still complete it.';
      }
      return 'Stop waiting for this terminal charge? This won’t automatically abort the swipe on the terminal. '
        + 'You’ll still need to press Cancel/Back on the device.';
    },
    confirmYesLabel() {
      const c = this.confirm;
      if (c && c.kind === 'close') return 'Close anyway';
      return c && c.kind === 'cancel-unconfirmed' ? 'Cancel charge' : 'Stop waiting';
    },
    confirmNoLabel() {
      const c = this.confirm;
      return c && c.kind === 'close' ? 'Keep waiting' : 'Not now';
    },
  },

  watch: {
    // The host's lock: the scrim and Escape are ignored while money is moving (the classic b-modal's
    // no-close-on-backdrop / no-close-on-esc).
    lockNeeded(now) {
      if (typeof this.setCloseLocked === 'function') this.setCloseLocked(!!now);
    },

    // A new step starts at the top: the setup step scrolls on a short screen, and the next one must not open
    // half-scrolled under the header.
    step() {
      this.$nextTick(() => {
        const body = this.$el && this.$el.querySelector ? this.$el.querySelector('[data-test="action-panel-body"]') : null;
        if (body) body.scrollTop = 0;
      });
    },

    // A question about a charge that has since moved on (it resolved, or was reset) is stale: drop it.
    terminalChargeState() {
      const c = this.confirm;
      if (!c) return;
      const stillAsking = c.kind === 'cancel-unconfirmed' ? this.terminalChargeState === 'unconfirmed' : this.terminalChargeInFlight;
      if (!stillAsking) this.confirm = null;
    },
  },

  methods: {
    dismiss() {
      if (this.dismissed) return;
      this.dismissed = true;
      this.$emit('closePanel', {});
    },

    /** The X and the phone's dimmed strip. */
    onClose() {
      this.closeModal();
    },

    /** The back arrow and "Change": the classic's goBackToSetup (it refuses mid-charge), but not mid-send. */
    onBack() {
      if (this.sendingInProgress) return;
      this.goBackToSetup();
    },

    /**
     * CollectPaymentModal.closeModal asked with a browser dialog while a charge was in flight. Here that
     * question is the footer's confirm row, and a send in flight (or the backend cancel) cannot be closed
     * over at all — the classic had no button for it either.
     */
    closeModal() {
      if (this.sendingInProgress || this.cancellingCharge) return;
      if (this.terminalChargeInFlight) {
        this.confirm = { kind: 'close' };
        return;
      }
      this.show = false;
    },

    /**
     * "Cancel charge". The classic asked first with a browser dialog (a different sentence for the unconfirmed
     * screen) and then ran the rest; here it opens the footer's confirm row, and doCancelCharge runs on Yes.
     * Strict `=== true` as in the classic: the in-flight button binds this bare and Vue passes the click event.
     */
    cancelInFlightCharge(unconfirmed) {
      if (this.cancellingCharge) return;
      this.confirm = { kind: unconfirmed === true ? 'cancel-unconfirmed' : 'cancel-charge' };
    },

    answerYes() {
      const c = this.confirm;
      this.confirm = null;
      if (!c) return;
      if (c.kind === 'close') this.show = false;
      else this.doCancelCharge();
    },

    answerNo() {
      this.confirm = null;
    },

    /**
     * A FAILED EMAIL MUST NOT SAY "SENT" — the same fix, at the same seam, as sendPanelCommon.sendEmailMsg.
     * `teamio/sendEmailMsg` swallows its error and resolves undefined, so the classic sendEmailOnly toasted
     * "Payment email sent" and closed, and sendBoth counted the email as sent. Throwing on a falsy result
     * lets the classic code do the right thing: sendEmailOnly's catch toasts the error and stays open,
     * sendBoth's allSettled sees a rejection ("SMS sent, but email failed"). The payload is the classic's.
     */
    async sendEmailMsg(payload) {
      const result = await this.$store.dispatch('teamio/sendEmailMsg', payload);
      if (!result) {
        const reason = this.$store.state.alert && this.$store.state.alert.message;
        throw new Error(typeof reason === 'string' && reason.trim() ? reason.trim() : 'Failed to send the payment email. Please try again.');
      }
      return result;
    },

    /**
     * CollectPaymentModal.cancelInFlightCharge from `if (this.cancellingCharge) return;` (its confirm dialog
     * removed) to the end — the same statements in the same order, same payload, same best-effort catch.
     */
    async doCancelCharge() {
      if (this.cancellingCharge) return;

      this.cancellingCharge = true;
      this.stopTerminalPolling();
      try {
        if (this.terminalPaymentID) {
          await this.cancelMoolahTerminalPayment({
            siteID: this.siteId,
            paymentID: this.terminalPaymentID,
          });
        }
      } catch (err) {
        // Best-effort. Even if the DB flip fails (network, race with
        // a paid-row already), we still want to leave the UI cleanly.
        console.warn('cancelInFlightCharge: backend cancel failed:', err && err.message);
      } finally {
        this.cancellingCharge = false;
        this.cancelledByOperator = true;
        this.terminalChargeState = 'cancelled';
        this.terminalChargeMessage = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;

// ---- Footer buttons -----------------------------------------------------------------------------
.cp-foot-main { @include ap-btn-primary; }
.cp-foot-secondary { @include ap-btn-secondary; }
.cp-foot-ghost { @include ap-btn-ghost; }

.cp-foot-danger {
  @include ap-btn-secondary;
  color: $pp-rose-ink;
  border-color: $pp-red-line;

  &:hover:not(:disabled) {
    border-color: $pp-red;
    background: $pp-red-bg;
  }
}

.ap-foot-note.is-warn { color: $pp-amber-deep; }

// The confirm row: the question gets its own line, the two answers sit under it.
.cp-confirm-text {
  flex: 1 1 100%;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: $pp-ink;
}

// ---- Fields -------------------------------------------------------------------------------------
// Sizes come ONLY from the ap-* mixins (14px, 16px on touch, in px — iOS focus-zoom).
.cp-select { @include ap-select; }
.cp-textarea { @include ap-textarea; }

.cp-hint { margin: 6px 0 0; }
.cp-hint code { padding: 1px 4px; border-radius: 4px; background: $pp-bg; }

// ---- Setup --------------------------------------------------------------------------------------
.cp-balance {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 20px $ap-gutter;
  border-bottom: 1px solid $pp-line;
  background: $pp-bg-2;

  @include ap-phone {
    padding-left: $ap-gutter-phone;
    padding-right: $ap-gutter-phone;
  }
}

.cp-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $pp-ink-3;
}

.cp-balance__v {
  margin-top: 4px;
  font-family: $pp-font;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.05;

  &.is-zero { color: $pp-ink-3; }
}

.cp-balance__src {
  margin-left: auto;
  padding-bottom: 5px;
  font-size: 12.5px;
  color: $pp-ink-3;
}

.cp-choices,
.cp-methods {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @include ap-phone { grid-template-columns: 1fr; }
}

// A choice card: the whole card is the radio's tap target.
.cp-choice {
  @include ap-row;
  align-items: flex-start;
  margin: 0;
  padding: 12px 14px;
  border-width: 1.5px;
  border-radius: $pp-radius-lg;

  &.is-on { border-color: $pp-brand-deep; background: $pp-brand-soft; }
  &.is-off { background: $pp-bg-2; cursor: not-allowed; }
}

.cp-choice__input,
.cp-term__input,
.cp-check__box {
  flex: none;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: $pp-brand-deep;
  cursor: inherit;
}

.cp-choice__body,
.cp-term__body,
.cp-check__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cp-choice__t { font-size: 14px; font-weight: 700; }
.cp-choice__d { font-size: 12.5px; line-height: 1.4; color: $pp-ink-3; }

// Why a card cannot be used — always readable, even on a greyed card.
.cp-why {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: $pp-amber-deep;
}

.cp-money { position: relative; }

.cp-money__sym {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 12px;
  display: flex;
  align-items: center;
  font-weight: 700;
  color: $pp-ink-3;
  pointer-events: none;
}

.cp-money__input { padding-left: 28px; }

// A payment method: icon, name, one line, and — when it cannot be used — the reason.
.cp-method {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-height: 48px;
  padding: 14px;
  border: 1.5px solid $pp-line;
  border-radius: $pp-radius-lg;
  background: $pp-surface;
  color: $pp-ink;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;

  &:hover:not(:disabled) { border-color: $pp-ink-4; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; }
  &.is-on { border-color: $pp-brand-deep; background: $pp-brand-soft; }

  &:disabled {
    cursor: not-allowed;
    background: $pp-bg-2;
  }

  &:disabled .cp-method__ic,
  &:disabled .cp-method__t,
  &:disabled .cp-method__d { opacity: 0.6; }
}

.cp-method__ic {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-bottom: 8px;
  border-radius: 9px;
  background: $pp-bg;
  color: $pp-ink-2;
  font-size: 18px;
}

.cp-method.is-on .cp-method__ic { background: $pp-surface; color: $teal-700; }
.cp-method__t { font-size: 14px; font-weight: 700; }
.cp-method__d { font-size: 12.5px; line-height: 1.4; color: $pp-ink-3; }

.cp-method__check {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 18px;
  color: $pp-brand-deep;
}

// ---- Configure ----------------------------------------------------------------------------------
.cp-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px $ap-gutter 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: $pp-bg;

  @include ap-phone {
    margin-left: $ap-gutter-phone;
    margin-right: $ap-gutter-phone;
  }
}

.cp-strip__ic {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: $pp-surface;
  color: $teal-700;
  font-size: 18px;
  box-shadow: $pp-shadow;
}

.cp-strip__text { min-width: 0; flex: 1 1 auto; }

.cp-strip__amt {
  font-family: $pp-font;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.cp-strip__sum {
  margin-top: 1px;
  font-size: 12.5px;
  color: $pp-ink-3;
  overflow-wrap: anywhere;
}

.cp-refresh { margin-left: auto; }

.cp-empty { display: flex; flex-direction: column; gap: 12px; }
.cp-empty__actions { display: flex; flex-wrap: wrap; gap: 8px; }

.cp-terms { display: flex; flex-direction: column; gap: 8px; }

.cp-term,
.cp-check {
  @include ap-row;
  align-items: flex-start;
  margin: 0;
  padding: 12px 14px;
  border-width: 1.5px;
  border-radius: $pp-radius-lg;

  &.is-on { border-color: $pp-brand-deep; background: $pp-brand-soft; }
}

.cp-term__n {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  font-size: 14px;
  font-weight: 700;
}

.cp-term__sn { font-size: 12.5px; color: $pp-ink-3; }

.cp-badge {
  padding: 1px 8px;
  border: 1px solid $amber-200;
  border-radius: $pp-radius-pill;
  background: $pp-amber-bg-2;
  color: $pp-amber-deep;
  font-size: 11px;
  font-weight: 700;
}

.cp-check__t { font-size: 14px; font-weight: 600; }
.cp-check__d { font-size: 12.5px; line-height: 1.4; color: $pp-ink-3; }
.cp-receipt-addr { display: flex; flex-direction: column; gap: 8px; padding-left: 30px; }

// ---- Terminal state -----------------------------------------------------------------------------
.cp-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 28px;
  text-align: center;

  @include ap-phone { padding: 32px 16px; }
}

.cp-spin {
  display: block;
  width: 56px;
  height: 56px;
  border: 4px solid $teal-100;
  border-top-color: $pp-brand-deep;
  border-radius: 50%;
  animation: cp-spin 0.9s linear infinite;
}

@keyframes cp-spin {
  to { transform: rotate(360deg); }
}

.cp-state__ic {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: $pp-slate-bg;
  color: $pp-slate-ink;
  font-size: 26px;

  &.is-ok { background: $pp-brand-soft; color: $teal-600; }
  &.is-warn { background: $pp-amber-bg-2; color: $pp-amber-deep; }
  &.is-bad { background: $pp-red-bg; color: $pp-rose-ink; }
  &.is-mute { background: $pp-slate-bg; color: $pp-slate-ink; }
}

.cp-state__t {
  margin-top: 4px;
  font-family: $pp-font;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.cp-state__amt {
  font-family: $pp-font;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.cp-state__d {
  max-width: 340px;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: $pp-ink-2;
}

.cp-state__n {
  max-width: 340px;
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: $pp-ink-3;
}

.cp-state__notice { max-width: 360px; text-align: left; }

.cp-breakdown {
  width: 100%;
  max-width: 360px;
  margin: 4px 0;
  overflow: hidden;
  border: 1px solid $pp-line;
  border-radius: $pp-radius-lg;
  text-align: left;
}

.cp-breakdown__row {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  font-size: 14px;

  & + & { border-top: 1px solid $pp-line; }
  span:last-child { margin-left: auto; font-weight: 600; }

  &.is-due {
    background: $pp-red-bg;
    font-weight: 700;

    span:last-child { font-weight: 800; color: $pp-rose-ink; }
  }
}

@media (prefers-reduced-motion: reduce) {
  .cp-spin { animation-duration: 3s; }
}
</style>
